// frontend/src/hooks/useCostImport.js
//
// The cost-import rail: start a run, then watch it.
//
// The import is a background pipeline run, not a request that finishes. It
// walks the store's whole Shopify-linked catalogue and writes per item —
// minutes on a large store — so POST /api/cost-import/run/ answers 202 as
// soon as the run is QUEUED and carries no counters. Everything the merchant
// sees after that comes from polling GET /api/cost-import/status/, whose
// `summary` only becomes non-null once the run has actually finished.

import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { costImport as costImportKey } from '../queries/keys';
import { useApiMutation } from '../queries/useApiMutation';
import { useBackgroundRunStatus } from './useBackgroundRunStatus';

/**
 * Queue a cost-import run for the current store.
 *
 * Resolves with the state of a run that has only just been queued:
 * `{outcome, status, in_progress, run_id, summary: null, ...}`. `outcome` is
 * `queued`, `queue_unavailable` (the row exists and the watchdog will re-send
 * it — still in progress), or `already_running` (an import was already in
 * flight; no second run was created). Nothing has been imported yet when this
 * resolves — read the status query for that.
 *
 * The answer is the status endpoint's state plus `outcome` (backend:
 * request_cost_import), so it becomes the cached status and the watcher picks
 * the new run up immediately instead of on its next tick.
 */
export function useCostImportStart() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const queryClient = useQueryClient();

  return useApiMutation({
    mutationFn: () => apiJson.post('/api/cost-import/run/', {}),
    // Nothing has been imported yet; the finished run announces the change.
    changes: [],
    onSuccess: (data) => queryClient.setQueryData(costImportKey.status(storeId), data),
  });
}

/**
 * Poll the current store's latest cost-import run.
 *
 * `enabled` lets the caller hold the poll until it has actually started a
 * run. Polling stops by itself when the backend stops reporting
 * `in_progress`, and the finished run's counters stay in the cache.
 *
 * A finished import has written each item's cost of record and, with it, the
 * default supplier's catalog cost (api/services/cost.py).
 */
export function useCostImportStatus({ enabled = true } = {}) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  return useBackgroundRunStatus({
    queryKey: costImportKey.status(storeId),
    url: '/api/cost-import/status/',
    changesOnFinish: [DATA.items, DATA.supplierLinks],
    enabled: enabled && !!storeId,
  });
}
